import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import UploadIcon from "@mui/icons-material/Upload";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { PageTab } from "documents/types/contract-documents/_views/contracts-page-tabs";
import LoadingButton from "_shared/_views/loading/loading-button";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import DocumentAPIService, { DocumentAPIServiceError } from "documents/_api/document-api-service";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import { filesize } from "filesize";
import _ from "lodash";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useNavigate } from "react-router";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";

import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import axios, { HttpStatusCode } from "axios";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import {
  getDocumentsByQuery,
  getDocumentsQueryByInterface,
  getErrorLoadingDocumentsByQuery,
  getIsLoadingDocumentsByQuery,
  populateDocumentsByQuery,
  updateDocumentsInterfaceQuery,
} from "documents/_stores/documents-redux-slice";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import { useSelector } from "react-redux";
import { DocumentFileType } from "documents/types/document-file-type";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import Loader from "_shared/_views/loading/loader";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const RowActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  width: "inherit",
}));
const NameLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: "pointer",
  fontSize: "1em",
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    textDecoration: "underline",
  },
}));
const NameContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  svg: {
    width: "24px",
  },
}));
const LabelChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const FilterButtons = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  },
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const UploadingLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));

export default function Contracts() {
  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = React.useState(PageTab.Conflicts);
  const [uploading, setUploading] = React.useState(false);
  const [mine, setMine] = React.useState(true);
  const [clientVendor, setClientVendor] = React.useState(false);

  const [showAlertBanner, setShowAlertBanner] = useState(false);
  const [showProgressBars, setShowProgressBars] = useState(false);
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});
  const [showSkeletons, setShowSkeletons] = React.useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();

  const interfaceName = "contractsIndex";
  const documentQuery = useSelector(getDocumentsQueryByInterface(interfaceName));
  const documents = useSelector(getDocumentsByQuery(documentQuery));
  const isLoading = useSelector(getIsLoadingDocumentsByQuery(documentQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingDocumentsByQuery(documentQuery));

  const labelsFilter = columnFilters.find((filter) => filter.id === "labels")?.value as string | undefined;
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  useEffect(() => {
    if (documentQuery) return;

    const defaultQuery = new DocumentQuery({
      orderParameters: new DocumentOrderParameters({
        orderBy: DocumentOrderField.Name,
        orderDir: OrderDirection.Ascending,
      }),
      filterParameters: new DocumentFilterParameters({
        name: columnFilters.find((filter) => filter.id === "name")?.value as string | undefined,
        contexts: labelsFilter ? [labelsFilter] : undefined,
        query: globalFilter,
      }),
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });

    dispatch(
      updateDocumentsInterfaceQuery({
        interfaceName,
        query: defaultQuery,
      }),
    );
  }, []);

  useEffect(() => {
    if (documentQuery && !isLoading && !errorLoadingQuery) {
      dispatch(
        populateDocumentsByQuery({
          session,
          query: documentQuery,
        }),
      );
    }
  }, [documentQuery]);

  useEffect(() => {
    if (!documents && documentQuery && !isLoading && !errorLoadingQuery) {
      dispatch(
        populateDocumentsByQuery({
          session,
          query: documentQuery,
        }),
      );
    }
  }, [documents]); // NOTE: This is kind of hacky, but works since we don't know the interface name in the documents-signalr-redux-middleware to trigger a correct populate dispatch when a document is added or deleted. This being here does NOT cause multiple API calls.

  useEffect(() => {
    if (isLoading && !isLoading && !showProgressBars) {
      setShowProgressBars(true);
    } else {
      setShowProgressBars(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (errorLoadingQuery) {
      setShowAlertBanner(true);
      setShowSkeletons(true);
      setShowProgressBars(false);
    } else {
      setShowAlertBanner(false);
    }
  }, [errorLoadingQuery]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (routeParams.tab) {
      setActiveTab(routeParams.tab as PageTab);
    } else {
      navigate(`/contracts/${PageTab.Conflicts}`);
    }

    let context = searchParams.get("context");
    if (context === null && routeParams.tab === PageTab.Policies) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "mine");
        return params;
      });
      context = "mine";
    }
    setMine(context === "mine");
    setClientVendor(context === "clientVendor");

    const contextFilters: string[] = [];
    if (context) {
      contextFilters.push(context);
    }
    if (routeParams.tab === PageTab.Policies) {
      contextFilters.push("policy");
    } else if (routeParams.tab === PageTab.Conflicts) {
      contextFilters.push("conflict");
    }
    const labelsFilter = columnFilters.find((filter) => filter.id === "labels")?.value as string | undefined;
    if (labelsFilter) {
      contextFilters.push(labelsFilter);
    }
    const orderingField = sorting.find((sort) =>
      Object.values(DocumentOrderField).includes(sort.id as DocumentOrderField),
    );

    const newQuery = new DocumentQuery({
      orderParameters: new DocumentOrderParameters({
        orderBy: orderingField ? (orderingField.id as DocumentOrderField) : DocumentOrderField.Created,
        orderDir: orderingField?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
      }),
      filterParameters: new DocumentFilterParameters({
        name: columnFilters.find((filter) => filter.id === "name")?.value as string | undefined,
        contexts: contextFilters,
        query: globalFilter,
      }),
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });

    if (newQuery.isEqualTo(documentQuery)) return;

    dispatch(
      updateDocumentsInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [routeParams, searchParams, pagination, activeTab, sorting, globalFilter, columnFilters]);

  function getUploadContext(tab: PageTab, isTemplate: boolean): string {
    if (isTemplate) {
      return tab === PageTab.Conflicts ? "ConflictsTemplate" : "PolicyTemplate";
    }
    return tab === PageTab.Conflicts ? "Conflicts" : "Policy";
  }

  const handleUploadError = (error: DocumentAPIServiceError) => {
    if (axios.isAxiosError(error.error) && error.error.response) {
      if (error.error.response.status === HttpStatusCode.Forbidden) {
        enqueueSnackbar(error.error.response.data, { variant: "error" });
        return;
      }
      if (
        error.error.response.status === HttpStatusCode.UnprocessableEntity ||
        error.error.response.status === HttpStatusCode.UnsupportedMediaType
      ) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
        return;
      }
    }
    enqueueSnackbar("Unknown error occurred during upload", {
      variant: "error",
    });
  };

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const file: File = event.currentTarget.files[0];

    const templateResponse = await confirm({
      title: "Create Template?",
      message: "Do you want to create a template or just upload the document?",
      okButtonText: "Create Template",
      alternativeOkText: "Just Upload",
      cancelButtonText: "Cancel",
    });

    if (templateResponse === ConfirmResponse.Cancel) return;

    setUploading(true);
    setShowAlertBanner(true);
    try {
      const isTemplate = templateResponse === ConfirmResponse.Ok;
      const context = getUploadContext(activeTab, isTemplate);
      const service = new DocumentAPIService(session);
      let topic = DocumentTopic.fromJSON({ context });
      await service.createDocument(file, file.name, topic ? [topic] : undefined);
      enqueueSnackbar("Uploaded document file", { variant: "success" });
    } catch (error) {
      handleUploadError(error as DocumentAPIServiceError);
    } finally {
      event.target.value = "";
      setShowAlertBanner(false);
      setUploading(false);
    }
  }

  /**
   * Downloads the document file with the given id
   * @param {Guid} id The id of the document file to download
   * @returns {Promise<void>}
   */
  async function handleDownload(id: Guid): Promise<void> {
    try {
      if (!id) {
        await Promise.reject(new Error("No document id provided"));
        return;
      }
      const service = new DocumentAPIService(session);
      const response = await service.downloadDocument(id);
      await downloadFile(response);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  }

  /**
   * Deletes the document file with the given id
   * @param {Guid} id The id of the document file to delete
   * @returns {Promise<void>}
   */
  async function handleDelete(id: Guid): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No document id provided"));
      return;
    }

    const response = await confirm({
      title: "Delete Document?",
      message: "Are you sure you want to delete this document?",
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const service = new DocumentAPIService(session);
      await service.deleteDocument(id);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete file", { variant: "error" });
    }
  }

  const renderNameColumn = (contract: Document | UnavailableDocument): React.ReactNode => {
    if ("issue" in contract) return <span>Unavailable Document</span>;

    const fileType = enumKeyOf(DocumentFileType, contract?.fileType?.toString()).toLocaleLowerCase();

    return (
      <NameContainer>
        <FileIcon extension={fileType} {...defaultStyles[fileType as keyof typeof defaultStyles]} />
        <NameLink onClick={() => handleDownload(contract.id)}>{contract.name?.value}</NameLink>
      </NameContainer>
    );
  };

  const renderLabelsColumn = (contract: Document | UnavailableDocument) => {
    if ("issue" in contract) return null;

    const labels = _.uniq(contract?.topics?.map((topic) => topic.context));
    return labels.map((label) => {
      if (!label) return null;
      return <LabelChip key={label} size="small" label={label} />;
    });
  };

  const columnHelper = createMRTColumnHelper<Document | UnavailableDocument>();
  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
      enableColumnFilter: true,
      enableSorting: true,
    }),
    columnHelper.accessor(renderLabelsColumn, {
      header: "Labels",
      id: "labels",
      enableColumnFilter: true,
      enableSorting: true,
    }),
    columnHelper.accessor(
      (contract) => {
        if ("issue" in contract) return "N/A";
        return contract?.topics?.reduce((acc, topic) => acc + (topic.entityClass === "Work.Proposal" ? 1 : 0), 0);
      },
      {
        header: "# of Proposals",
        id: "numberOfProposals",
        enableSorting: false,
        enableColumnFilter: false,
      },
    ),
    columnHelper.accessor(
      (contract) => {
        if ("issue" in contract) return "N/A";
        return contract?.created?.format("MM/DD/YY hh:mm A");
      },
      {
        header: "Created",
        id: "created",
        enableColumnFilter: false,
      },
    ),
    columnHelper.accessor(
      (contract) => {
        if ("issue" in contract) return "N/A";
        return filesize(contract?.fileSize ?? 0);
      },
      {
        header: "Size",
        id: "fileSize",
        enableSorting: true,
        enableColumnFilter: false,
      },
    ),
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: documents?.data ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <LoadingButton
              loading={uploading}
              startIcon={<UploadIcon />}
              onClick={() => documentFileUploaderRef.current?.click()}
            >
              {`Upload ${activeTab}`}
            </LoadingButton>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton aria-label="Upload" color="primary" onClick={() => documentFileUploaderRef.current?.click()}>
            <UploadIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Contracts</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/contracts/${newValue}/`)}
            >
              <Tab value={PageTab.Conflicts} label="Conflicts" />
              <Tab value={PageTab.Policies} label="Policies" />
            </Tabs>
            {activeTab === PageTab.Policies && (
              <FilterButtons>
                <Chip
                  icon={<FilterListIcon />}
                  label="My Policies"
                  color="primary"
                  variant={mine ? "filled" : "outlined"}
                  onClick={() => {
                    if (mine) return;
                    setMine((prevValue) => !prevValue);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (mine) {
                        params.set("context", "clientVendor");
                      } else {
                        params.set("context", "mine");
                      }
                      return params;
                    });
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label={`My ${session.context?.viewingAsVendor ? "Clients'" : "Vendors'"} Policies`}
                  color="primary"
                  variant={clientVendor ? "filled" : "outlined"}
                  onClick={() => {
                    if (clientVendor) return;
                    setClientVendor((prevValue) => !prevValue);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (clientVendor) {
                        params.set("context", "mine");
                      } else {
                        params.set("context", "clientVendor");
                      }
                      return params;
                    });
                  }}
                />
              </FilterButtons>
            )}
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActions: ({ row }) => {
      const isDisabled =
        "issue" in row.original ||
        row.original.hasSystemPermissions ||
        row.original.topics?.some((topic) => topic.entityClass === "Work.Proposal");
      return (
        <RowActionsContainer>
          <Tooltip title="Download">
            <span>
              <IconButton onClick={() => handleDownload(row.original.id)}>
                <DownloadIcon color="primary" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isDisabled ? "Unable to delete: File in use" : "Delete"}>
            <span>
              <IconButton onClick={() => handleDelete(row.original.id)} disabled={isDisabled}>
                <DeleteIcon color={isDisabled ? "disabled" : "error"} />
              </IconButton>
            </span>
          </Tooltip>
        </RowActionsContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: showAlertBanner
      ? {
        color: errorLoadingQuery ? "error" : "info",
        children: errorLoadingQuery 
          ? "Error loading data" 
          : <UploadingLoader size={24} text="Uploading..." />,
      }
      : undefined,
    rowCount: documents?.totalElements ?? 0,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      globalFilter,
      isLoading: isLoading,
      pagination,
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  });

  if (!sessionPermissions?.includesAccessToContracts) return <NotAuthorized />;

  return (
    <>
      <MaterialReactTable table={table} />
      <input type="file" hidden={true} ref={documentFileUploaderRef} onChange={handleDocumentFileUpload} />
    </>
  );
}
